import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";
import { FiHome, FiVideo, FiUser } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n";
import { getMainTabId } from "../../layouts/mainTabRoutes";
import { useTabSwitch } from "../../layouts/TabSwitchContext";
import { isOnboardingIncomplete } from "../../features/onboarding/onboardingSession";
import "./BottomTabs.css";

const tabs = [
  { id: "home", labelKey: "nav.home", icon: FiHome, to: "/" },
  { id: "reels", labelKey: "nav.reels", icon: FiVideo, to: "/reels" },
  { id: "profile", labelKey: "nav.profile", icon: FiUser, to: "/profile-details", requiresAuth: true },
];

const AUTH_PATHS = new Set(["/auth", "/signin", "/register"]);

function getUrlTabId(pathname, locationState, isAuthenticated, user = null) {
  if (pathname.startsWith("/onboarding") && isOnboardingIncomplete(isAuthenticated, user)) {
    return "profile";
  }

  if (AUTH_PATHS.has(pathname)) {
    return locationState?.navTab ?? null;
  }

  return getMainTabId(pathname);
}

export default function BottomTabs() {
  const { isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  const { navTab, switchTab } = useTabSwitch();
  const location = useLocation();
  const urlTab = getUrlTabId(location.pathname, location.state, isAuthenticated, user);
  const [displayTab, setDisplayTab] = useState(() => urlTab ?? navTab ?? "home");
  const pickedTabRef = useRef(null);

  // Sync from URL/context on deep links / back — never override a tab the user just tapped.
  useLayoutEffect(() => {
    if (!urlTab) return;

    if (pickedTabRef.current) {
      if (urlTab === pickedTabRef.current) {
        pickedTabRef.current = null;
      }
      return;
    }

    if (urlTab === navTab) {
      setDisplayTab(navTab);
    }
  }, [urlTab, navTab]);

  const activeTab = displayTab;
  const activeIndex = tabs.findIndex((tab) => tab.id === activeTab);

  const handleTabPointerDown = useCallback(
    (tab) => {
      pickedTabRef.current = tab.id;
      flushSync(() => {
        setDisplayTab(tab.id);
      });
      switchTab(tab.id, { requiresAuth: tab.requiresAuth, isAuthenticated, user });
    },
    [isAuthenticated, switchTab],
  );

  const handleTabClick = useCallback((event) => {
    event.preventDefault();
  }, []);

  return (
    <nav className="bottom-nav-wrapper" aria-label="Main navigation">
      <div className="bottom-nav-pill" style={{ "--tab-count": tabs.length }}>
        <div
          className="bottom-nav-bubble"
          style={{
            transform: activeIndex >= 0 ? `translateX(${activeIndex * 100}%)` : "translateX(0)",
            opacity: activeIndex >= 0 ? 1 : 0,
          }}
          aria-hidden
        />
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          const isCenter = tab.id === "reels";
          return (
            <button
              key={tab.id}
              type="button"
              className={`tab-item${isActive ? " active" : ""}${isCenter ? " tab-item--center" : ""}`}
              onPointerDown={() => handleTabPointerDown(tab)}
              onClick={handleTabClick}
              aria-current={isActive ? "page" : undefined}
            >
              <Icon className="tab-icon" aria-hidden />
              <span className="tab-label">{t(tab.labelKey)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
